'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ContentCard,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  MenuItem,
  Label,
  cn,
  toast,
} from '@crabs-ai/ds-react';
import {
  MoreHoriz,
  CloudDownload,
  Download,
  Delete,
  PlayArrowFill,
  PauseFill,
} from '@crabs-ai/web-icons';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useDeleteGtsVideo } from '@/features/gts/api/delete-gts-video';
import { GtsGalleryVideoItem } from '@/features/gts/model/gts-types';
import { usePostContentCopyToAssetApi } from '@/shared/apis/content/post-content-copy-to-asset';
import { useGetSpaceMeApi } from '@/features/my-space/api/get-space-me';
import { convertMillisecondsToTimeFormat } from '@/shared';
import { DtoGtsVideoStatus } from '@/shared/constants/dto';
import { DtoContentType } from '@/shared/constants';
import { AnimatedDots } from '@/shared/ui/AnimatedDots';
import ImageWithFallback from '@/shared/ui/images/ImageWithFallback';
import { formatOutputObjectDate } from '@/shared/utils/date-format';
import {
  ASSET_DRAG_TYPE,
  AssetDragData,
  setCurrentDragData,
} from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/asset/AssetContentItem';

const AI_VIDEO_PLAY_EVENT = 'ai-video-item-play';

interface AiVideoGeneratedItemProps {
  video: GtsGalleryVideoItem;
  projectId: number;
  onClick?: (video: GtsGalleryVideoItem) => void;
  showTypeLabel?: boolean;
}

export default function AiVideoGeneratedItem({
  video,
  projectId,
  onClick,
  showTypeLabel = false,
}: AiVideoGeneratedItemProps) {
  const t = useTranslations();
  const { data: space } = useGetSpaceMeApi();
  const { mutate: copyToAsset } = usePostContentCopyToAssetApi();
  const deleteVideoMutation = useDeleteGtsVideo(projectId);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const videoIdRef = useRef(video.id);

  const isDeleting = deleteVideoMutation.isPending;
  const isProcessing = video.status === DtoGtsVideoStatus.PROCESSING;
  const isCompleted = video.status === DtoGtsVideoStatus.COMPLETED;
  const isFailed = video.status === DtoGtsVideoStatus.FAILED;

  // 다른 아이템 재생 시 현재 아이템 정지 (배타적 재생)
  useEffect(() => {
    const handleOtherPlay = (e: CustomEvent<string>) => {
      if (e.detail !== videoIdRef.current && isPlaying) {
        if (videoRef.current) {
          videoRef.current.pause();
        }
        setIsPlaying(false);
      }
    };

    window.addEventListener(
      AI_VIDEO_PLAY_EVENT,
      handleOtherPlay as EventListener,
    );
    return () => {
      window.removeEventListener(
        AI_VIDEO_PLAY_EVENT,
        handleOtherPlay as EventListener,
      );
    };
  }, [isPlaying]);

  const handlePlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause();
          setIsPlaying(false);
        } else {
          window.dispatchEvent(
            new CustomEvent(AI_VIDEO_PLAY_EVENT, { detail: video.id }),
          );
          videoRef.current.currentTime = 0;
          videoRef.current
            .play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch(() => {
              setIsPlaying(false);
            });
        }
      }
    },
    [isPlaying, video.id],
  );

  const handleMouseEnter = useCallback(() => {
    if (!isCompleted) return;
    setIsHovered(true);
  }, [isCompleted]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleMediaEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // 드래그 시작 핸들러
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!isCompleted) {
        e.preventDefault();
        return;
      }

      setCurrentDragData(null);

      const dragData: AssetDragData = {
        itemId: video.contentId ?? Date.now(),
        name: `AI Video #${video.id}`,
        publicUrl: video.publicUrl ?? '',
        thumbnailPublicUrl: video.thumbnailUrl ?? null,
        contentType: DtoContentType.video,
        duration: video.duration ?? null,
        extension: video.extension ?? 0, // 0 = mp4
      };

      e.dataTransfer.setData(ASSET_DRAG_TYPE, JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = 'copy';
      setIsDragging(true);
      setCurrentDragData(dragData);

      // 드래그 이미지 설정
      const dragElement = document.createElement('div');
      dragElement.textContent = `AI Video #${video.id}`;
      dragElement.style.cssText = `
        position: absolute;
        top: -9999px;
        left: -9999px;
        padding: 8px 12px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        font-size: 12px;
        font-weight: 500;
        border-radius: 6px;
        white-space: nowrap;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      `;
      document.body.appendChild(dragElement);
      e.dataTransfer.setDragImage(dragElement, 0, 0);

      requestAnimationFrame(() => {
        document.body.removeChild(dragElement);
      });
    },
    [isCompleted, video],
  );

  // 드래그 종료 핸들러
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setCurrentDragData(null);
  }, []);

  const handleDelete = useCallback(() => {
    deleteVideoMutation.mutate(video.id, {
      onSuccess: () => {
        toast({
          type: 'success',
          description: t('Editor.ai-video.delete-success'),
          toastOptions: { position: 'top-center' },
        });
      },
      onError: () => {
        toast({
          type: 'error',
          description: t('Editor.ai-video.delete-fail'),
          toastOptions: { position: 'top-center' },
        });
      },
    });
  }, [video.id, deleteVideoMutation, t]);

  const handleSaveToSpace = useCallback(() => {
    const spaceId = space?.data?.id;
    if (!spaceId || !video.contentId) return;

    copyToAsset(
      { contentId: video.contentId, currentSpaceId: spaceId },
      {
        onSuccess: () => {
          toast({
            type: 'success',
            description: t('Editor.ai-video.save-to-space-success'),
            toastOptions: { position: 'top-center' },
          });
        },
        onError: () => {
          toast({
            type: 'error',
            description: t('Editor.ai-video.save-to-space-fail'),
            toastOptions: { position: 'top-center' },
          });
        },
      },
    );
  }, [space?.data?.id, video.contentId, copyToAsset, t]);

  const handleDownloadToComputer = useCallback(async () => {
    if (!video.publicUrl) return;

    const downloadName = `ai-video-${video.id}.mp4`;

    toast({
      type: 'success',
      description: t('Editor.ai-video.download-start'),
      toastOptions: { position: 'top-center' },
    });

    try {
      // cross-origin URL에서는 download 속성이 무시되므로 blob으로 변환
      const response = await fetch(video.publicUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);

      toast({
        type: 'success',
        description: t('Editor.ai-video.download-success'),
        toastOptions: { position: 'top-center' },
      });
    } catch {
      toast({
        type: 'error',
        description: t('Editor.ai-video.download-fail'),
        toastOptions: { position: 'top-center' },
      });
    }
  }, [video.publicUrl, video.id, t]);

  const titleNode = useMemo(() => {
    if (isDeleting) {
      return (
        <span>
          {t('Editor.ai-video.deleting')}
          <AnimatedDots />
        </span>
      );
    }
    if (isProcessing) {
      return (
        <span>
          {t('Editor.ai-video.generating')}
          <AnimatedDots />
        </span>
      );
    }
    return `AI Video #${video.id}`;
  }, [isDeleting, isProcessing, video.id, t]);

  const metadata = useMemo(
    () => (
      <span>
        {formatOutputObjectDate({
          createdAt: video.createdAt,
          updatedAt: video.createdAt,
          t: t,
        })}
      </span>
    ),
    [video.createdAt, t],
  );

  const mediaNode = useMemo(
    () => (
      <div
        className="relative aspect-square h-full w-full"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isCompleted && (
          <video
            ref={videoRef}
            src={video.publicUrl}
            preload="none"
            className={cn(
              'h-full w-full bg-black object-contain',
              !isPlaying && 'hidden',
            )}
            onEnded={handleMediaEnded}
          />
        )}
        <div className={cn(isCompleted && isPlaying && 'hidden')}>
          <ImageWithFallback
            key={video.thumbnailUrl ?? video.publicUrl}
            src={video.thumbnailUrl ?? video.publicUrl}
            alt="video thumbnail"
            fill
          />
        </div>
        {showTypeLabel && (
          <div className="absolute top-0 right-0 z-20 p-2xs">
            <Label
              color="primary"
              isHighlight
              size="small"
              type="default"
            >
              {t('Editor.asset-item.video')}
            </Label>
          </div>
        )}
        <AnimatePresence>
          {isCompleted && isHovered && (
            <motion.button
              type="button"
              onClick={handlePlayClick}
              className={cn(
                'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                'flex size-xl items-center justify-center rounded-full',
                'bg-black/60 transition-all hover:bg-black/80',
                'cursor-pointer',
              )}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.1, ease: 'easeOut' }}
            >
              {isPlaying ? (
                <PauseFill className="size-md [&_path]:fill-white" />
              ) : (
                <PlayArrowFill className="size-md [&_path]:fill-white" />
              )}
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    ),
    [
      video.publicUrl,
      video.thumbnailUrl,
      isCompleted,
      isPlaying,
      isHovered,
      showTypeLabel,
      handlePlayClick,
      handleMediaEnded,
      handleMouseEnter,
      handleMouseLeave,
    ],
  );

  const menuTriggerNode = useMemo(() => {
    if (isProcessing) return undefined;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <MoreHoriz className="size-5 [&_path]:fill-sys-cont-neutral-default" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="end"
          sideOffset={4}
          className="inset-shadow-basic-md"
        >
          <MenuItem
            type="general"
            icon={<CloudDownload />}
            text={t('Editor.ai-video.save-to-space')}
            onClick={(e) => {
              e.stopPropagation();
              handleSaveToSpace();
            }}
          />
          {isCompleted && (
            <MenuItem
              type="general"
              icon={<Download />}
              text={t('Editor.ai-video.download-to-computer')}
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadToComputer();
              }}
            />
          )}
          <MenuItem
            type="general"
            icon={<Delete />}
            text={t('Editor.ai-video.delete-video')}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }, [
    isProcessing,
    isCompleted,
    t,
    handleSaveToSpace,
    handleDownloadToComputer,
    handleDelete,
  ]);

  const metadataLabelNode = useMemo(() => {
    if (!video.duration) return null;

    return (
      <div
        className="absolute w-full"
        style={{ bottom: 0, left: 0 }}
      >
        <div className="flex w-full gap-2xs p-2xs">
          <Label
            color="neutral"
            type="overlay"
            leadingIcon={
              <PlayArrowFill className="size-sm [&_path]:fill-sys-cont-inverse-light" />
            }
            size="small"
          >
            {convertMillisecondsToTimeFormat(video.duration)}
          </Label>
        </div>
      </div>
    );
  }, [video.duration]);

  return (
    <div
      draggable={isCompleted}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={cn(isCompleted && 'cursor-grab', isDragging && 'opacity-50')}
    >
      <ContentCard
        state={
          isProcessing || isDeleting
            ? 'loading'
            : isFailed
              ? 'failed'
              : 'default'
        }
        loadingType={isProcessing || isDeleting ? 'infinite' : undefined}
        title={titleNode}
        metadata={metadata}
        mediaNode={mediaNode}
        metadataLabelNode={metadataLabelNode}
        menuTriggerNode={menuTriggerNode}
        onClick={() => {
          if (isCompleted) {
            onClick?.(video);
          }
        }}
      />
    </div>
  );
}
